import { Box } from '@material-ui/core';
import { useTheme, withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import { CheckOutlined, CloseOutlined, ErrorOutlineRounded as ExclamationCircleOutlined, SwapHoriz } from '@material-ui/icons';
import { Switch } from 'antd';
import { select } from 'd3';
import { mediaUrls } from 'globalconfig/constants/mediaUrls';
import { checkIsAdmin, getFilterLocaleValues, hasEnabledFeature, loadStyle } from 'globalutils/fmUtils';
import moment from 'moment';
import React, { lazy, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { changeStatusLine } from './changeStatusLine';
import { disableEditOWColumns, enableEditOWColumns, statusList } from './config';
import { generateGrid } from './generateGrid';
import {
  findMinutesFromTime,
  getNewGraphPoints,
  getObjectKeyValue,
  getStatusFromPoint,
  resetDataList,
} from './getNewGraphPoints';
import { checkServiceProvider } from './serviceProviderConfig';
import CoDriverEventReassignmentModal from '@/pages/web/EditDailyLog/CoDriverEventReassignmentModal';
import {
  hasActiveReassignmentRequests,
  isEligibleDrivingEntry,
} from '@/pages/web/EditDailyLog/coDriverEventReassignmentUtils';
import UdReassignmentModal from '@/pages/web/EditDailyLog/UdReassignmentModal';
import UdReassignIcon from '@/pages/web/EditDailyLog/UdReassignIcon';
import {
  isEligibleUdDrivingEntry,
  hasActiveUdReassignmentRequests,
} from '@/pages/web/EditDailyLog/udReassignmentUtils';
import ParentStyled from './style';

const Styled = loadStyle(ParentStyled, 'modules/DailyLog');
const layout = window?.$environment?.CURRENT_LAYOUT;
const SuccessIcon = lazy(() => import(`globalcomponents/SuccessIcon/${layout}`));
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const Table = lazy(() => import(`globalcomponents/UI/Table/${layout}`));
const Icon = lazy(() => import(`globalcomponents/UI/Icon/${layout}`));
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const Modal = require(`globalcomponents/UI/Modal/${layout}`);
const SvgIcon = lazy(() => import(`globalcomponents/SvgIcon/${layout}`));

const DailyLog = ({
  gridWidth,
  gridHeight,
  origin,
  yGap,
  yData,
  xData,
  dataList: propsData,
  setDailyLogEntries,
  originalData,
  assignedDriver,
  viewOnly,
  pageViewOnly,
  setFinalListForUpdate,
  finalListForUpdate,
  udEdit,
  drivers,
  unformattedDriverList,
  enableEditOW,
  dailyLogDetails,
  providerMode,
  setProviderMode,
  getDailyLogDetails,
  coDriverEventReassignment,
  logIdPub,
  createCoDriverEventReassignment,
  udReassignment,
  companyDrivers,
  createUdReassignment,
  apiStatus,
  fetchDailyLogDetailsApiId,
}) => {
  const isAdmin = checkIsAdmin();
  const match = useParams();
  const { i18n } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [dataList, setDataList] = useState([]);
  const [reassignEntry, setReassignEntry] = useState(null);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);

  const SERVICE_PROVIDER = window.$environment.SERVICE_PROVIDER;
  const isSpireonNew = layout === 'SpireonNew';
  const [udReassignEntry, setUdReassignEntry] = useState(null);
  const [udReassignModalOpen, setUdReassignModalOpen] = useState(false);

  const isSpireonProvider = SERVICE_PROVIDER === 'spireon' || layout === 'SpireonNew';

  const getEntryForRowIndex = (rowIndex) => {
    const point = editedDataList[rowIndex * 2];
    if (!point) return null;
    return dataList.find((item) => item.id === point.key);
  };

  const isPageViewOnly = pageViewOnly ?? viewOnly;
  const hasAnyActiveReassignment =
    hasActiveReassignmentRequests(coDriverEventReassignment) ||
    hasActiveUdReassignmentRequests(udReassignment);

  const canShowReassignForRow = (rowIndex) => {
    if (!isSpireonNew || isPageViewOnly) return false;
    if (!coDriverEventReassignment?.enabled || hasAnyActiveReassignment) {
      return false;
    }
    const entry = getEntryForRowIndex(rowIndex);
    return isEligibleDrivingEntry(entry, coDriverEventReassignment);
  };

  const canShowUdReassignForRow = (rowIndex) => {
    if (!isSpireonProvider || isPageViewOnly || udEdit) return false;
    if (!udReassignment?.enabled || hasAnyActiveReassignment) {
      return false;
    }
    const entry = getEntryForRowIndex(rowIndex);
    return isEligibleUdDrivingEntry(entry, udReassignment);
  };

  useEffect(() => {
    if (propsData) setDataList(propsData);
  }, [propsData]);

  //Grid cell size
  const gridCellWidth = gridWidth || 13;
  const gridCellHeight = gridHeight || 13;

  //Graph origin
  const leftTopCoordinate = origin || { x: 10, y: 10 };
  //Graph width and height
  const graphHeight = gridCellHeight * yData.length;
  const graphWidth = gridCellWidth * (xData.length - 1);
  const svgWidth = gridCellWidth * (xData.length - 1) + leftTopCoordinate.x;

  //Y gap from origin to first status text
  const extraYGapInStatusLine = yGap || 5;

  //To display x and y axis texts
  const yAxis = { x: 2, y: gridCellHeight };
  const xAxis = { x: gridCellWidth, y: 4 };
  const svgViewBox = enableEditOW ? '0 0 335 89' : '0 0 335 75';

  //Y axis values
  const [yValues, setYValues] = useState([]);

  useEffect(() => {
    setYValues(
      yData.map((data, index) => {
        return {
          key: data.value,
          text: data.label,
          x: yAxis.x,
          y: (index + 1) * yAxis.y + 5,
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yData]);

  const startTime = '12:00am';
  const endTime = '12:00am';

  //X axis values
  const xValues = xData.map((data, index) => {
    return {
      text: data,
      x: (index + 1) * xAxis.x - 4,
      y: xAxis.y,
    };
  });

  const topStatus = yData[0];

  //Newly added rows
  const [editedDataList, setEditedDataList] = useState([]);

  //Selected row
  const [selectedRow, setSelectedRow] = useState(-1);
  const [newRow, setNewRow] = useState(0);

  // Initial value for edit overlay area
  const [editingArea, setEditingArea] = useState({
    lx: leftTopCoordinate.x,
    ly: leftTopCoordinate.y + extraYGapInStatusLine,
    ltime: startTime,
    rx: svgWidth,
    ry: leftTopCoordinate.y + extraYGapInStatusLine,
    rtime: endTime,
  });

  const [isDragged, setIsDragged] = useState(false);
  const [canEditRowByGraph, setCanEditRowByGraph] = useState(true);
  //Values to plot the step graph
  const [stepGraphPoints, setStepGraphPoints] = useState([
    {
      x: leftTopCoordinate.x,
      y: 15,
      time: startTime,
      status: { ...topStatus },
      note: '',
      stateModifier: {
        label: i18n.t('common.pleaseSelect'),
        value: 'please',
      },
      location: '',
      driverId: { ...assignedDriver },
      readOnly: false,
    },
    {
      x: svgWidth,
      y: leftTopCoordinate.y + extraYGapInStatusLine,
      time: endTime,
      status: { ...topStatus },
      note: '',
      stateModifier: {
        label: i18n.t('common.pleaseSelect'),
        value: 'please',
      },
      location: '',
      driverId: { ...assignedDriver },
      readOnly: false,
    },
  ]);

  const updateField = (payload) => {
    let source = [...finalListForUpdate];
    source = source.map((item) => {
      if ((!item[payload.field]?.label && payload.field === 'driverId') || payload.field === 'coDriverId') {
        item[payload.field] = payload.data;
      }
      if (payload.data && !item[payload.field] && (payload.field === 'location' || payload.field === 'note')) {
        item[payload.field] = payload.data;
      }
      return item;
    });
    setFinalListForUpdate(source);
  };

  useEffect(() => {
    let result = [];

    if (dataList && dataList.length && yValues.length) {
      dataList.map((item) => {
        const stateModifier = item.oilFieldWaiting ? item.stateModifier : item.status;
        const y = getObjectKeyValue(yValues, 'text', stateModifier, 'y') - 3;
        const statusKey = getObjectKeyValue(yValues, 'text', item.status, 'key');

        const common = {
          status: {
            label: item.status,
            value: statusKey,
          },
          note: item.note,
          stateModifier: (item.stateModifier && {
            label: checkServiceProvider('stateModifier', SERVICE_PROVIDER, false) ? 'Yes' : item.stateModifier,
            value: 'OW',
          }) || {
            label: checkServiceProvider('stateModifier', SERVICE_PROVIDER, false) ? 'No' : i18n.t('common.pleaseSelect'),
            value: 'please',
          },
          oilFieldWaiting: item.oilFieldWaiting,
          location: item.location,
          readOnly: item.readOnly,
          udReadOnly: item.udReadOnly,
          eldData: item.eldData,
          key: item.id,
          minutes_from: item.minutes_from,
          minutes_to: item?.minutes_to,
          eventId: item.eventId,
          time_from: item.time_from,
          time_to: item.time_to,
        };

        result.push({
          x:
            item.time_from === startTime && item.minutes_from !== 1440
              ? leftTopCoordinate.x
              : item.minutes_from == 1440
              ? findGraphPointsFromTime('11:59pm')
              : item.time_from
              ? findGraphPointsFromTime(item.time_from)
              : '',
          y,
          time: item.time_from,
          driverId:
            udEdit && item.driverId
              ? {
                  label: unformattedDriverList && unformattedDriverList[item.driverId],
                  value: item.driverId,
                }
              : item.driverId === 0
              ? { label: i18n.t('common.pleaseSelect'), value: 0 }
              : { ...assignedDriver },
          ...common,
        });

        result.push({
          x:
            item.time_to === startTime && item.minutes_to === 0
              ? leftTopCoordinate.x
              : item.time_to === endTime
              ? svgWidth
              : item.minutes_to >= 1440
              ? findGraphPointsFromTime('11:59pm')
              : item.time_to
              ? findGraphPointsFromTime(item.time_to)
              : '',
          y,
          time: item.time_to,
          driverId: udEdit
            ? {
                label: unformattedDriverList && unformattedDriverList[item.driverId],
                value: item.driverId,
              }
            : { ...assignedDriver },
          ...common,
        });
      });

      if (udEdit) {
        result = result.map((item) => {
          if (udEdit) {
            return item.status?.label === 'DRIVING' && item.driverId?.label !== 'Unidentified Driver' ? item : null;
          } else return item.status?.label === 'DRIVING' && item.driverId?.label === 'Unidentified Driver' ? item : null;
        });
        result = result.filter((item) => item);

        setEditedDataList([...result]);
        setFinalListForUpdate([...result]);
      } else {
        const filteredPoints = result.filter((o) => {
          return (
            ((o.minutes_to <= 1440 || !o.minutes_to) && (o.minutes_from <= 1440 || !o.minutes_from)) ||
            ((o.minutes_to >= 1440 || !o.minutes_to) && o.minutes_from <= 1440)
          );
        });

        setStepGraphPoints([...filteredPoints]);
        setEditedDataList([...result]);
        setFinalListForUpdate([...result]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataList, yValues, providerMode]);

  const [ModalListObject, setModalListObject] = useState({
    header: '',
    content: () => {},
  });
  const buttonStatusStyle = selectedRow !== -1 ? 'button--status-normal' : 'button--disabled';
  const [buttonUpdateStyle, setButtonUpdateStyle] = useState(selectedRow !== -1 ? 'button--update-normal' : 'button--disabled');
  const [buttonUpdateGraphStyle, setButtonUpdateGraphStyle] = useState('button--update-normal');
  //Update when editing area changes
  useEffect(() => {
    const stepGraph = select('svg > .stepGraph')._groups[0][0];
    //If edit icon gets clicked
    if (stepGraph !== null) {
      changeStatusLine({
        event: null,
        editingArea,
        isDragged,
        setEditingArea,
        theme,
        origin,
        graphHeight,
        canEditRowByGraph,
        graphWidth,
        svgWidth,
        gridCellWidth,
        setIsDragged,
      });
    }

    if (selectedRow > -1) {
      yValues.map((y) => {
        const node = document.getElementById(y.key);
        node.className = node.className.replace('active', '');
      });
      const status = getStatusFromPoint({ value: getObjectKeyValue(yValues, 'y', editingArea.ly + 3, 'key'), yData });
      const node = document.getElementById(status.value);
      node.className = node.className + ' active';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingArea]);

  //Update based on the graph values
  useEffect(() => {
    const stepGraph = select('svg > .stepGraph')._groups[0][0];
    //If page load happens
    if (stepGraph === null && yValues.length) {
      generateGrid({ svgViewBox, yValues, xValues, theme, origin, yData, xData, gridWidth, gridHeight });
    }
    //Re draw graph when change step points
    const hasUpdated = hasEditedTheList();
    if (!hasUpdated && yValues.length) drawGraph();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepGraphPoints]);

  const timeInTwelve = (time) => {
    const isTimeInTwelve = time.split(':')[0] === '12' && !time.includes('12:00');
    return isTimeInTwelve ? time.replace('12:', '0:') : time;
  };

  const findGraphPointsFromTime = (time) => {
    //Find the time corresponding to stick position
    if (time) {
      const oneHr = gridCellWidth;
      const oneMinute = gridCellWidth / 60;
      time = timeInTwelve(time);
      const hrs = parseInt(time.split(':')[0], 10);

      const minutes = parseInt(time.split(':')[1].substring(0, 2), 10);
      const timeIn24 = time.includes('pm') > 0 && time !== '12:00pm' ? hrs + 12 : hrs;

      return timeIn24 * oneHr + minutes * oneMinute + leftTopCoordinate.x;
    } else return '';
  };

  const checkIfEdited = (source) => {
    let needUpdate = false;
    let temp = [];
    for (let index = 0; index < source.length; index++) {
      let point = source[index];
      let isEdited = point.time.includes('edited') > 0;
      if (point.deleted) {
        if (source[index + 1]?.deleted) {
          if (index === 0) {
            temp[0] = {
              ...source[2],
              x: point.x,
              time: point.time,
            };
            temp[1] = {
              ...source[3],
            };
            index = 3;
          } else if (index === source.length - 2) {
            temp[index - 1] = {
              ...temp[index - 1],
              x: source[index + 1].x,
              time: source[index + 1].time,
            };
          } else {
            temp[index - 1] = {
              ...temp[index - 1],
              x: source[index + 2].x,
              time: source[index + 2].time,
            };
          }
          needUpdate = true;
        } else {
        }
      } else if (isEdited) {
        const time = point.time.replace('edited', '');
        point = {
          ...point,
          x: findGraphPointsFromTime(time),
          time: time,
        };
        temp.push(point);
        needUpdate = true;
      } else if (point.status.edited) {
        point = {
          ...point,
          status: { value: point.status.value, label: point.status.label },
        };
        temp.push(point);

        needUpdate = true;
      } else {
        temp.push(point);
      }
    }

    return { needUpdate, temp };
  };

  const hasEditedTheList = () => {
    return editedDataList.some(
      (el) => (typeof el.time === 'string' && el.time.includes('edited')) || el.status?.edited || el.deleted
    );
  };

  const updateGraph = (needUpdate, temp) => {
    //If all times are entered
    if (validateRow()) {
      if (selectedRow > -1) {
        getNewGraphPoints({
          editingArea,
          editedDataList,
          selectedRow,
          yValues,
          providerMode,
          yData,
          dailyLogDetails,
          setDailyLogEntries,
        });
        setIsDragged(false);
      } else if (needUpdate || temp.length) {
        const result = resetDataList({ source: [...temp], dailyLogDetails });
        setDailyLogEntries({
          data: [...result],
          asArray: true,
        });
      }

      //Clear all after an event update
      select('svg > .overlay').remove();

      setSelectedRow(-1);
      setNewRow(0);
      setButtonUpdateStyle('button--disabled');
      setButtonUpdateGraphStyle('button--update-normal');
      setCanEditRowByGraph(true);
    }
  };

  const drawGraph = () => {
    //Clear existing svg
    select('svg > .overlay').remove();
    select('svg > .stepGraph').remove();

    select('.svg-content')
      .append('g')
      .attr('class', 'stepGraph')
      .data([stepGraphPoints])
      .append('polyline')
      .attr('points', function (d) {
        return d
          .map(function (d) {
            return [d.x, d.y].join(',');
          })
          .join(' ');
      })
      .attr('stroke', theme.palette.secondary.main)
      .attr('fill', 'none')
      .style('stroke-width', '1.5');
  };

  const handleTextEdit = (value, index, type) => {
    let graphValues = editedDataList.length ? [...editedDataList] : [...stepGraphPoints];
    graphValues[index][type] = value;
    graphValues[index + 1][type] = value;
    setEditedDataList([...graphValues]);
    setFinalListForUpdate([...graphValues]);
  };

  const handleTimeEdit = (value, index, type) => {
    let graphValues = editedDataList.length ? [...editedDataList] : [...stepGraphPoints];

    const isValid = validateTime(value);
    graphValues[index] = {
      ...graphValues[index],
      time: !isValid ? value : value + 'edited',
    };

    if (type === 'time_from') {
      graphValues[index].minutes_from = findMinutesFromTime(value);
      if (index > 0) {
        graphValues[index - 1].minutes_to = findMinutesFromTime(value);
      }
    }
    if (type === 'time_to') {
      graphValues[index].minutes_to = findMinutesFromTime(value);
      if (index < graphValues.length && graphValues[index + 1]) {
        graphValues[index + 1].minutes_from = findMinutesFromTime(value);
      }
    }
    if (graphValues.length) {
      const point = graphValues[index];
      if (index === 0) {
        point.time = startTime;
        point.minutes_from = 0;
        if (graphValues[index + 1]) graphValues[index + 1].minutes_from = 0;
      } else if (index === editedDataList.length - 1) {
        point.time = endTime;
        point.minutes_to = 1440;
        graphValues[index - 1].minutes_from = 1440;
      } else if (index % 2 === 0) {
        const prev = graphValues[index - 1];
        prev.time = point.time;
        prev.minutes_from = findMinutesFromTime(value);
        graphValues[index - 1].minutes_to = findMinutesFromTime(value);
        if (index > 1) {
          graphValues[index - 2].minutes_to = findMinutesFromTime(value);
        }
        graphValues[index - 1] = { ...prev };
      } else if (index % 2 === 1) {
        const next = graphValues[index + 1];
        next.time = point.time;
        next.minutes_from = findMinutesFromTime(value);
        graphValues[index - 1].minutes_to = findMinutesFromTime(value);
        if (index > 1) {
          graphValues[index + 2].minutes_from = findMinutesFromTime(value);
        }
        graphValues[index + 1] = { ...next };
      }
      graphValues[index] = point;

      setEditedDataList(graphValues);
      setFinalListForUpdate(graphValues);
    }
  };

  const confirmEdit = (row, index) => {
    const item = editedDataList[index * 2];
    const next = editedDataList[index * 2 + 1];
    const y =
      getObjectKeyValue(yValues, 'key', item.stateModifier?.value === 'OW' ? item.stateModifier.value : item.status.value, 'y') -
      3;
    setTimeout(
      () =>
        setEditingArea({
          ly: y,
          ry: y,
          ltime: item.time,
          rtime: next.time,
          lx: item.time === startTime ? leftTopCoordinate.x : findGraphPointsFromTime(item.time),
          rx: next.time === endTime ? svgWidth : findGraphPointsFromTime(next.time),
        }),
      500
    );
    setNewRow(0);
    setSelectedRow(index);
  };

  const handleOnEdit = (row, index) => {
    if (validateRow()) {
      setButtonUpdateStyle('button--update-normal');
      setButtonUpdateGraphStyle('button--disabled');
      const { temp } = checkIfEdited(editedDataList);
      const result = resetDataList({ source: [...temp], dailyLogDetails });
      setDailyLogEntries({
        data: [...result],
        asArray: true,
      });
      updateDataList(() => {
        confirmEdit(row, index);
      });
    }
  };

  const showDeleteConfirmation = (row, index) => {
    setModalListObject({
      content:
        editedDataList.length === 2
          ? () => (
              <div className="successpopup">
                <div className="imageSection">
                  <SvgIcon value={mediaUrls.confirmIcon} alt="Minimum 1 row is required" />
                </div>
                <h2>{i18n.t('common.minimumRow')}</h2>
                <div className="deletePopupBtn">
                  <Button
                    label={i18n.t('common.ok')}
                    onClick={() => {
                      setSelectedRow(-1);
                      closeModal();
                    }}
                  ></Button>
                </div>
              </div>
            )
          : () => (
              <div className="successpopup">
                <div className="imageSection">
                  <SvgIcon value={mediaUrls.confirmIcon} alt="Are you sure" />
                </div>
                <h2>{i18n.t('common.sureConfirm')}</h2>
                <div className="deletePopupBtn">
                  <div className="cancelBtn">
                    <Button
                      label={i18n.t('common.cancel')}
                      type={'reset'}
                      onClick={() => {
                        setSelectedRow(-1);
                        closeModal();
                      }}
                    ></Button>
                  </div>
                  <div className="deletebtn">
                    <Button
                      label={i18n.t('common.deletePopup')}
                      onClick={() => {
                        confirmDelete(row, index);
                      }}
                    ></Button>
                  </div>
                </div>
              </div>
            ),
    });
    clickedModal();
  };

  const handleOnDelete = (row, index) => {
    if (validateRow()) {
      updateDataList(() => {
        showDeleteConfirmation(row, index);
      });
    }
  };

  const closeModal = () => {
    setOpen(false);
  };

  const confirmDelete = (row, index) => {
    let points = [...editedDataList];
    points[index * 2].deleted = true;
    points[index * 2 + 1].deleted = true;
    const { temp } = checkIfEdited([...points]);
    setEditedDataList(temp);
    setFinalListForUpdate(temp);

    setModalListObject({
      header: '',
      content: () => {
        return (
          <>
            <SuccessIcon />
            <div className="deletedRow">{i18n.t('Deleted!')}</div>
            <Button
              label={i18n.t('common.ok')}
              onClick={() => {
                closeModal();
              }}
            />
          </>
        );
      },
    });
  };

  const handleStatusChange = (value, index, type) => {
    if (selectedRow === -1 && validateRow()) {
      let points = editedDataList.length ? [...editedDataList] : [...stepGraphPoints];
      let firstPoint = {
        ...points[index],
        [type]: { ...value, edited: true },
        y: getObjectKeyValue(yValues, 'key', value.value, 'y') - 3,
      };
      let secondPoint = {
        ...points[index + 1],
        [type]: value,
        y: getObjectKeyValue(yValues, 'key', value.value, 'y') - 3,
      };
      if (enableEditOW && value.value !== 'OFF' && type === 'status' && firstPoint?.stateModifier?.value === 'OW') {
        firstPoint.stateModifier = {
          label: i18n.t('common.pleaseSelect'),
          value: 'please',
          edited: true,
        };
        secondPoint.stateModifier = { label: i18n.t('common.pleaseSelect'), value: 'please' };
      } else if (type === 'stateModifier' && value.value === 'OW') {
        firstPoint.status = { label: i18n.t('OFF_DUTY'), value: 'OFF', edited: true };
        secondPoint.status = { label: i18n.t('OFF_DUTY'), value: 'OFF' };
      }
      if (points[index].status.value == 'DR' && providerMode && firstPoint.status.value != 'DR') {
        firstPoint.readOnly = false;
      }
      points[index] = firstPoint;
      points[index + 1] = secondPoint;

      setEditedDataList([...points]);
      setFinalListForUpdate([...points]);

      setButtonUpdateGraphStyle('button--update-normal');
    }
  };

  const handleSelectChange = (value, index, type) => {
    if ((selectedRow === -1 && validateRow() && !udEdit) || udEdit) {
      let points = editedDataList.length ? [...editedDataList] : [...stepGraphPoints];
      points[index][type] = { ...value };
      points[index + 1][type] = { ...value };
      setEditedDataList([...points]);
      setFinalListForUpdate([...points]);
    }
  };

  const validateTime = (time) => {
    time = time?.replace('edited', '');
    const isValid = time?.match(/(10|11|12|[0-9]):[0-5][0-9][a|p]m/);
    return isValid?.length ? true : false;
  };

  const validateRow = () => {
    let elements = document.getElementsByClassName('time_wrapper');
    for (let i = 0, len = elements.length; i < len; i++) {
      elements[i].classList.remove('dailyLogDateError');
    }
    for (let index = 0; index < editedDataList.length; index++) {
      const element = editedDataList[index];
      const isValid = validateTime(element.time);
      if (!isValid) {
        return false;
      }
      if (index !== editedDataList.length - 1) {
        let v = document.getElementById(`timeToDailyLog${index + 1}`);
        const nextTime1 = editedDataList[index + 1]['time'].replace('edited', '');

        const current1 = editedDataList[index]['time'].replace('edited', '');

        const nextTime = moment(nextTime1, 'h:mma').toDate();
        const currentTime = moment(current1, 'h:mma').toDate();

        if (!udEdit && moment(nextTime).unix() < moment(currentTime).unix() && index + 1 !== editedDataList.length - 1) {
          v.classList.add('dailyLogDateError');
          return false;
        }
      }
    }
    return true;
  };

  function getSuggestions({ isNormal, isOWEditable }) {
    if (isNormal) {
      return isOWEditable
        ? [
            { label: 'Yes', value: 'OW' },
            { label: 'No', value: 'please' },
          ]
        : [{ label: 'No', value: 'please' }];
    }

    return isOWEditable
      ? [
          { label: i18n.t('common.pleaseSelect'), value: 'please' },
          { label: i18n.t('OIL_FIELD_WAITING'), value: 'OW' },
        ]
      : [{ label: i18n.t('common.pleaseSelect'), value: 'please' }];
  }


  const getRowInfo = (list, rows) => {
    let row = {};
    for (let index = 0; index < list.length; index++) {
      const point = list[index];
      const time = point?.time?.includes('edited') > 0 ? point?.time?.replace('edited', '') : point?.time;
      const previous = list[index - 1];
      const next = list[index + 1];
      if (index % 2 === 0) {
        if (point?.noEdit) {
          row.noEdit = true;
        }
        row.readOnly = point?.readOnly;
        row.driverId = !viewOnly ? (
          <Select
            onChange={(value, type) => {
              if (
                udEdit ||
                (providerMode && point.eldData) ||
                (!providerMode && !point?.readOnly && !viewOnly)
              ) {
                handleSelectChange(value, index, 'driverId');
              }
              udEdit &&
                updateField({
                  data: value,
                  field: 'driverId',
                  id: point.id,
                });
            }}
            placeholder={i18n.t('common.pleaseSelect')}
            value={point?.driverId || ''}
            suggestions={udEdit ? drivers : [assignedDriver]}
            disabled={
              providerMode && point.eldData
                ? false
                : providerMode && !point.eldData
                ? true
                : (point?.udReadOnly && udEdit) || (point?.readOnly && !udEdit) || viewOnly || selectedRow > -1
            }
          />
        ) : point?.driverId?.value === 0 ? (
          '-'
        ) : (
          point?.driverId?.label
        );
        row.stateModifier = !viewOnly ? (
          <Select
            onChange={(value) => {
              if ((providerMode && point.eldData) || (!providerMode && !point?.readOnly && !viewOnly)) {
                handleStatusChange(value, index, 'stateModifier');
              }
            }}
            disabled={
              providerMode && point.eldData
                ? false
                : providerMode && !point.eldData
                ? true
                : point?.readOnly || viewOnly || selectedRow > -1
            }
            value={
              point?.stateModifier || {
                label: i18n.t('common.pleaseSelect'),
                value: 'please',
              }
            }
            placeholder={i18n.t('common.pleaseSelect')}
            suggestions={getSuggestions({
              isNormal: checkServiceProvider('stateModifier', SERVICE_PROVIDER, false),
              isOWEditable: enableEditOW && point?.status.value === 'OFF',
            })}
          />
        ) : point?.stateModifier?.value === 'please' ? (
          '-'
        ) : (
          point.stateModifier.label
        );
        row.location = (
          <Input
            value={point?.location || ''}
            style={{ color: 'white' }}
            handleOnChange={(value) => {
              if (
                !point?.eldData &&
                (((selectedRow === -1 && validateRow() && !point?.readOnly) && !providerMode) ||
                  (providerMode && point.eldData))
              ) {
                handleTextEdit(value, index, 'location');
              }
            }}
            disabled={
              point?.eldData
                ? true
                : providerMode && point.eldData
                  ? false
                  : providerMode && !point.eldData
                    ? true
                    : udEdit ||
                    (point?.udReadOnly && udEdit) ||
                    (point?.readOnly && !udEdit) ||
                    viewOnly ||
                    selectedRow > -1
            }
            type="text"
            onBlur={(event) => {
              udEdit && !point?.eldData &&
                updateField({
                  data: event,
                  field: 'location',
                  id: point.id,
                });
            }}
          />
        );
        row.note = (
          <>
            <Input
              value={point?.note || ''}
              style={{ color: 'white' }}
              handleOnChange={(value, event) => {
                if (
                  !point?.eldData &&
                  !udEdit &&
                  (((selectedRow === -1 && validateRow() && !point?.readOnly) && !providerMode) ||
                    (providerMode && point.eldData))
                ) {
                  handleTextEdit(value, index, 'note');
                } else {
                  event.preventDefault();
                }
              }}
              type="text"
              disabled={
                point?.eldData
                  ? true
                  : providerMode && point.eldData
                    ? false
                    : providerMode && !point.eldData
                      ? true
                      : udEdit ||
                      (point?.udReadOnly && udEdit) ||
                      (point?.readOnly && !udEdit) ||
                      viewOnly ||
                      selectedRow > -1
              }
              onBlur={(event) => {
                udEdit && !point?.eldData &&
                  updateField({
                    data: event,
                    field: 'note',
                    id: point.id,
                  });
              }}
            />
          </>
        );
        row.status = (
          <>
            {!viewOnly ? (
              <Select
                onChange={(value) => {
                  if ((providerMode && point.eldData) || (!providerMode && !point?.readOnly && !viewOnly)) {
                    handleStatusChange(value, index, 'status');
                  }
                }}
                name="status"
                value={getFilterLocaleValues([point?.status || ''], i18n)}
                suggestions={getFilterLocaleValues(statusList, i18n)}
                disabled={
                  providerMode && point.eldData
                    ? false
                    : providerMode && !point.eldData
                    ? true
                    : point?.readOnly || viewOnly || selectedRow > -1 || udEdit
                }
              />
            ) : point?.status?.label ? (
              point.status.label
            ) : (
              '-'
            )}
          </>
        );
        let minHr = 0;
        let minMin = 0;
        let maxHr = 23;
        let maxMin = 59;
        if (list[index + 2] && list[index + 2].readOnly) {
          maxHr = list[index + 1].time.split(':')[1].includes('am')
            ? parseInt(list[index + 1].time.split(':')[0], 10) === 12
              ? 0
              : list[index + 1].time.split(':')[0]
            : parseInt(list[index + 1].time.split(':')[0], 10) === 12
            ? 12
            : parseInt(list[index + 1].time.split(':')[0], 10) + 12;
          maxMin = parseInt(list[index + 1].time.split(':')[1].slice(0, -2), 10);
        }
        if (providerMode && list[index + 1] && list[index + 1].minutes_to) {
          maxHr = Math.floor(list[index + 1].minutes_to / 60);
          maxMin = Math.floor(list[index + 1].minutes_to % 60);
        }
        if (providerMode && list[index - 1] && list[index + 1].minutes_from) {
          minHr = Math.floor(list[index - 1].minutes_from / 60);
          minMin = Math.floor(list[index - 1].minutes_from % 60);
        }

        row.time_from = (
          <div
            id={`timeToDailyLog${index}`}
            style={{
              color: 'white',
              border: validateTime(point?.time) ? '0px' : 'solid 2px red',
            }}
            className="time_wrapper"
          >
            <DatePicker
              onChange={(data) => {
                if (data) {
                  let elements = document.getElementsByClassName('time_wrapper');
                  for (let i = 0, len = elements.length; i < len; i++) {
                    elements[i].classList.remove('dailyLogDateError');
                  }
                  let v = document.getElementById(`timeToDailyLog${index}`);
                  const nextTime1 =
                    list[index + 1]['time'].includes('edited') > 0
                      ? list[index + 1]['time'].replace('edited', '')
                      : list[index + 1]['time'];

                  const nextTime = moment(nextTime1, 'h:mma').toDate();

                  if (index !== 0 && index !== list.length - 2) {
                    if (!udEdit && moment(nextTime).unix() < moment(data).unix()) {
                      v.classList.add('dailyLogDateError');
                    } else {
                      v.classList.contains('dailyLogDateError') && v.classList.remove('dailyLogDateError');
                    }
                  }
                  if ((!point?.readOnly && !providerMode) || (providerMode && point.eldData)) {
                    const time = moment(data).format('h:mma');

                    handleTimeEdit(time, index, 'time_from');
                  }
                }
              }}
              showPopperArrow={false}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={checkServiceProvider('timeIntervals', SERVICE_PROVIDER, false)}
              timeCaption=""
              selected={time ? moment(time, 'h:mma').toDate() : null}
              dateFormat="h:mma"
              disabled={
                providerMode && point.eldData
                  ? false
                  : providerMode && !point.eldData
                  ? true
                  : (previous && previous.readOnly) || point?.readOnly || viewOnly || selectedRow > -1 || udEdit
              }
              minTime={moment().set({ hour: minHr, minute: minMin }).toDate()}
              maxTime={moment().set({ hour: maxHr, minute: maxMin }).toDate()}
            />
          </div>
        );
      } else {
        let minHr = 0;
        let minMin = 0;
        let maxHr = 23;
        let maxMin = 59;
        if (list[index].minutes_from) {
          minHr = Math.floor(list[index].minutes_from / 60);
          minMin = Math.floor(list[index].minutes_from % 60);
        }
        if (providerMode && list[index + 1] && list[index + 1].minutes_to) {
          maxHr = Math.floor(list[index + 1].minutes_to / 60);
          maxMin = Math.floor(list[index + 1].minutes_to % 60);
        }
        if (list[index - 2] && list[index - 2].readOnly) {
          minHr = list[index - 1].time.split(':')[1]?.includes('am')
            ? parseInt(list[index - 1].time.split(':')[0], 10) === 12
              ? 0
              : list[index - 1].time.split(':')[0]
            : parseInt(list[index - 1].time.split(':')[0], 10) === 12
            ? 12
            : parseInt(list[index - 1].time.split(':')[0], 10) + 12;
          minMin = parseInt(list[index - 1].time.split(':')[1]?.slice(0, -2), 10);
        }
        row.time_to = (
          <div
            id={`timeToDailyLog${index}`}
            style={{
              color: 'white',
              border: validateTime(point?.time) ? '0px' : 'solid 2px red',
            }}
            className="time_wrapper"
          >
            <DatePicker
              onChange={(data) => {
                if (data) {
                  let elements = document.getElementsByClassName('time_wrapper');
                  for (let i = 0, len = elements.length; i < len; i++) {
                    elements[i].classList.remove('dailyLogDateError');
                  }
                  let v = document.getElementById(`timeToDailyLog${index}`);
                  const prevTime1 =
                    list[index - 1]['time'].includes('edited') > 0
                      ? list[index - 1]['time'].replace('edited', '')
                      : list[index - 1]['time'];
                  const prevTime = moment(prevTime1, 'h:mma').toDate();

                  if (index !== list.length - 1) {
                    if (!udEdit && moment(prevTime).unix() > moment(data).unix()) {
                      v.classList.add('dailyLogDateError');
                    } else {
                      v.classList.contains('dailyLogDateError') && v.classList.remove('dailyLogDateError');
                    }
                  }
                  if ((!point?.readOnly && !providerMode) || (providerMode && point.eldData)) {
                    const time = moment(data).format('h:mma');
                    handleTimeEdit(time, index, 'time_to');
                  }
                }
              }}
              as={DatePicker}
              showPopperArrow={false}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={checkServiceProvider('timeIntervals', SERVICE_PROVIDER, false)}
              timeCaption=""
              selected={time ? moment(time, 'h:mma').toDate() : null}
              dateFormat="h:mma"
              disabled={
                providerMode && point.eldData
                  ? false
                  : providerMode && !point.eldData
                  ? true
                  : (next && next.readOnly) || point?.readOnly || viewOnly || selectedRow > -1 || udEdit
              }
              minTime={moment().set({ hour: minHr, minute: minMin }).toDate()}
              maxTime={moment().set({ hour: maxHr, minute: maxMin }).toDate()}
            />
          </div>
        );
        rows.push(row);
        row = {};
      }
    }
  };

  const generateStatusRows = () => {
    const rows = [];
    getRowInfo(editedDataList.length && editedDataList, rows);

    return rows;
  };

  const clickedModal = (e) => {
    setOpen(true);
  };

  const addNewRowToList = (list) => {
    if (!list[list?.length - 1]?.readOnly && !providerMode) {
      setNewRow(newRow + 1);
      const points = list.length ? [...list] : [...stepGraphPoints];
      //Need confirmation
      const lastIndex = editedDataList.length - 1;
      let point = {
        ...points[lastIndex],
        x: '',
        time: '',
      };
      points[lastIndex] = { ...point };

      const common = {
        stateModifier: {
          label: i18n.t('common.pleaseSelect'),
          value: 'please',
        },
        driverId: { ...assignedDriver },
        y: getObjectKeyValue(yValues, 'key', 'OFF', 'y') - 3,
        status: { label: i18n.t('OFF_DUTY'), value: 'OFF' },
        readOnly: false,
        noEdit: true,
        minutes_from: 0,
        minutes_to: 1440,
      };

      const firstPoint = {
        time: '',
        location: null,
        x: '',
        ...common,
      };
      const secondPoint = {
        location: '',
        x: svgWidth,
        time: endTime,
        ...common,
      };
      points.push({ ...firstPoint });
      points.push({ ...secondPoint });

      setEditedDataList([...points]);
      setFinalListForUpdate([...points]);
    }
  };

  const addNewRow = () => {
    selectedRow < 0 && updateDataList(() => addNewRowToList(editedDataList));
  };

  const updateDataList = (callback) => {
    if (validateRow()) {
      callback();
    }
  };

  const editStatus = (event) => {
    if (selectedRow !== -1 && canEditRowByGraph) {
      yValues.map((y) => {
        const node = document.getElementById(y.key);
        node.className = node.className.replace('active', '');
      });
      const className = event.target.offsetParent.className;
      event.target.offsetParent.className = className.includes('active')
        ? event.target.offsetParent.className
        : event.target.offsetParent.className + ' active';
      changeStatusLine({
        event,
        editingArea,
        isDragged,
        setEditingArea,
        theme,
        origin,
        graphHeight,
        canEditRowByGraph,
        graphWidth,
        svgWidth,
        gridCellWidth,
        setIsDragged,
      });
    }
  };

  const columns = enableEditOW && !udEdit ? enableEditOWColumns : disableEditOWColumns;

  const [showTopMessage, setShowTopMessage] = useState(false);

  const toggleProvider = () => {
    setOpen(true);
    setModalListObject({
      content: () => (
        <div className="successpopup">
          <div className="imageSection">
            <SvgIcon value={mediaUrls.confirmIcon} alt="Are you sure" />
          </div>
          <h2>
            {i18n.t(
              !providerMode
                ? 'Turning ON provider mode will allow you to edit ELD entries, but all other changes made will be lost'
                : 'Turning off provider mode will lead to the loss of edited ELD entries'
            )}
          </h2>
          <div className="deletePopupBtn">
            <div className="cancelBtn">
              <Button
                label={i18n.t('common.cancel')}
                type={'reset'}
                onClick={() => {
                  closeModal();
                }}
              ></Button>
            </div>
            <div className="deletebtn">
              <Button
                label={i18n.t('Yes, proceed!')}
                onClick={() => {
                  getDailyLogDetails({
                    apiId: 'getDailyLogDetailsAPI',
                    data: { id: match.id },
                    providerMode: !providerMode,
                    udEdit: false,
                  });
                  setProviderMode((prev) => {
                    return !prev;
                  });
                  closeModal();
                }}
              ></Button>
            </div>
          </div>
        </div>
      ),
    });
  };

  const checkEldEntries = () => {
    return originalData?.dailyLogEntries?.some((ele) => {
      return ele.readOnly && ele.status == 'DRIVING';
    });
  };
  const HtmlTooltip = withStyles((theme) => ({
    tooltip: {
      maxWidth: 220,
      fontSize: '17px',
    },
  }))(Tooltip);

  return (
    <Styled.ChartWrapper>
      <div className="quickFilter"></div>
      {hasEnabledFeature('chartEnabled') && !udEdit && (
        <>
          {selectedRow > -1 && showTopMessage && (
            <div className="editmodeNotification" style={{ color: 'white' }}>
              You are in edit mode. Please click update events before attempting to make a new edit.
            </div>
          )}
          <div id="chartContainer" className="svg-container"></div>
          {!viewOnly ? (
            <div className={isAdmin ? 'updateEventsectionWrapper' : ''}>
              {isAdmin ? (
                <div className={`providerWrapper`}>
                  <div className={`${checkEldEntries() ? '' : 'disableProvider'} providerWrapper`}>
                    <Switch
                      checked={providerMode}
                      onChange={toggleProvider}
                      checkedChildren={<CheckOutlined />}
                      unCheckedChildren={<CloseOutlined />}
                    />
                    <div onClick={toggleProvider} className="providerLabel">
                      Provider Mode
                    </div>
                  </div>

                  {checkEldEntries() ? null : (
                    <HtmlTooltip
                      id="custom-tool"
                      title={'Provider mode is disabled due to the absence of ELD entries in this log'}
                      arrow
                      placement="right"
                    >
                      <ExclamationCircleOutlined className="infoIcon" />
                    </HtmlTooltip>
                  )}
                </div>
              ) : null}
              <Box display="flex" justifyContent="flex-end" alignItems="center" className="updateEventsection">
                {yValues.map((y) => (
                  <React.Fragment key={y.text}>
                    <Button
                      className={'button-status ' + buttonStatusStyle}
                      dataValue={y.y.toString()}
                      label={i18n.t(y.text)}
                      id={y.key}
                      onClick={editStatus}
                    />
                  </React.Fragment>
                ))}
                <Box id="update_graph">
                  <Button
                    className={'button-update ' + buttonUpdateStyle}
                    label={i18n.t('logs.UpdateEvents')}
                    onClick={() => {
                      const { needUpdate, temp } = checkIfEdited(editedDataList);
                      selectedRow > -1 && updateGraph(needUpdate, temp);
                    }}
                  />
                </Box>
                <Button
                  className={'button-update ' + buttonUpdateGraphStyle}
                  label={i18n.t('logs.UpdateGraph')}
                  id="updategraph"
                  onClick={() => {
                    const { needUpdate, temp } = checkIfEdited(editedDataList);

                    selectedRow === -1 && updateGraph(needUpdate, temp);
                  }}
                />
              </Box>
            </div>
          ) : null}
        </>
      )}
      <Styled.StatusModifier>
        <Table
          type="client"
          title=""
          columns={columns}
          data={generateStatusRows()}
          rowSelection={() => {
            const isTimeValid = validateRow();
            if (selectedRow > -1 || !isTimeValid) return;
          }}
          actions={
            udEdit
              ? []
              : [
                  {
                    icon: (row, index, disabled) => {
                      const previous = editedDataList[index * 2 - 1];
                      const current = editedDataList[index * 2];
                      const next = editedDataList[index * 2 + 2];
                      const isReadOnlyRow =
                        (providerMode && !current?.eldData) ||
                        (!providerMode && (previous?.readOnly || current?.readOnly || (next && next.readOnly)));
                      const isBtnDisabled = disabled || selectedRow > -1;
                      const showEdit =
                        !viewOnly &&
                        !row.noEdit &&
                        hasEnabledFeature('chartEnabled') &&
                        !checkServiceProvider('tableIcon', SERVICE_PROVIDER, false);
                      const showUdReassign = canShowUdReassignForRow(index);
                      const showCoDriverReassign = canShowReassignForRow(index);
                      const showDelete = !viewOnly && !isReadOnlyRow;

                      if (!showEdit && !showUdReassign && !showCoDriverReassign && !showDelete) return null;

                      return (
                        <div className="dailyLogRowActions">
                          {showEdit && (
                            <Icon
                              src={isReadOnlyRow ? mediaUrls?.notFull : mediaUrls?.edit}
                              style={{
                                width: '20px',
                                height: '20px',
                                overflow: 'visible',
                              }}
                              onClick={() => {
                                setShowTopMessage(
                                  (providerMode && !current.eldData) ||
                                    (!providerMode && (previous?.readOnly || current?.readOnly || next?.readOnly))
                                    ? false
                                    : true
                                );
                                setCanEditRowByGraph(
                                  (providerMode && !current.eldData) ||
                                    (!providerMode && (previous?.readOnly || current?.readOnly || next?.readOnly))
                                    ? false
                                    : true
                                );
                                if (
                                  (providerMode && current.eldData) ||
                                  (!providerMode &&
                                    ((!disabled && selectedRow === -1) ||
                                      (!disabled && (previous?.readOnly || current?.readOnly || next?.readOnly))))
                                )
                                  handleOnEdit(row, index);
                              }}
                              className={isReadOnlyRow ? 'viewIconWrapper' : 'iconContainer'}
                            />
                          )}
                          {showUdReassign && (
                            <Tooltip title={i18n.t('logs.reassignToUd')} placement="top">
                              <span className="iconContainer udReassignIconWrapper">
                                <UdReassignIcon
                                  className={`udReassignIconBtn${isBtnDisabled ? ' udReassignIconBtnDisabled' : ''}`}
                                  style={{ width: 28, height: 28 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isBtnDisabled) return;
                                    const entry = getEntryForRowIndex(index);
                                    setUdReassignEntry(entry);
                                    setUdReassignModalOpen(true);
                                  }}
                                />
                              </span>
                            </Tooltip>
                          )}
                          {showCoDriverReassign && (
                            <Tooltip title={i18n.t('logs.reassignToCoDriver')} placement="top">
                              <span className="iconContainer reassignEventIconWrapper">
                                <SwapHoriz
                                  className={`reassignEventBtn${isBtnDisabled ? ' reassignEventBtnDisabled' : ''}`}
                                  style={{ fontSize: 28, width: 28, height: 28 }}
                                  onClick={() => {
                                    if (isBtnDisabled) return;
                                    const entry = getEntryForRowIndex(index);
                                    setReassignEntry(entry);
                                    setReassignModalOpen(true);
                                  }}
                                />
                              </span>
                            </Tooltip>
                          )}
                          {showDelete && (
                            <Tooltip title={i18n.t('logs.deleteLogEntry')} placement="top">
                              <span className="iconContainer">
                                <Icon
                                  src={mediaUrls?.deleteIcon}
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    overflow: 'visible',
                                  }}
                                  onClick={() => !disabled && selectedRow < 0 && handleOnDelete(row, index)}
                                />
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      );
                    },
                  },
                ]
          }
          i18n={i18n}
          noPagination
          noTableActions
        />
      </Styled.StatusModifier>
      {!viewOnly && !udEdit && (
        <Box id="addNewRow">
          <Button
            label={i18n.t('logs.addNewRow')}
            tooltipTxt={selectedRow >= 0 ? 'Update Events before adding new row' : ''}
            onClick={addNewRow}
            className={`button-add ${selectedRow >= 0 || providerMode ? 'btn-disabled' : ''}`}
          />
        </Box>
      )}

      <Modal.default open={open} setOpen={setOpen} data={ModalListObject} isSuccess />
      {createCoDriverEventReassignment && (
        <CoDriverEventReassignmentModal
          open={reassignModalOpen}
          setOpen={setReassignModalOpen}
          entry={reassignEntry}
          coDriverEventReassignment={coDriverEventReassignment}
          companyDrivers={originalData?.companyDrivers}
          logIdPub={logIdPub}
          i18n={i18n}
          createCoDriverEventReassignment={createCoDriverEventReassignment}
          getDailyLogDetails={getDailyLogDetails}
          apiStatus={apiStatus}
          fetchDailyLogDetailsApiId={fetchDailyLogDetailsApiId}
        />
      )}
      {createUdReassignment && (
        <UdReassignmentModal
          open={udReassignModalOpen}
          setOpen={setUdReassignModalOpen}
          entry={udReassignEntry}
          companyDrivers={companyDrivers || originalData?.companyDrivers}
          logIdPub={logIdPub}
          i18n={i18n}
          createUdReassignment={createUdReassignment}
          getDailyLogDetails={getDailyLogDetails}
          apiStatus={apiStatus}
          fetchDailyLogDetailsApiId={fetchDailyLogDetailsApiId}
        />
      )}
    </Styled.ChartWrapper>
  );
};

export default DailyLog;
